import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ANSPRACHEN, ERGEBNISSE_JAGDSCHEIN, ERGEBNISSE_SCHIESSNACHWEIS, JAGDSCHEINARTEN, WILDARTEN, type Wildart } from "@dj/domain/drueckjagd";
import { Dialog } from "../design/Dialog";
import { Ankreuzfeld, Auswahl, Klappliste, Textfeld } from "../design/Formular";
import { Abweisung, Status } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { Bestaetigung, RueckgaengigLeiste, useRueckgaengig } from "../design/Rueckgaengig";
import { Symbol } from "../design/Symbol";
import { useZurueck } from "../design/zurueck";
import { istAntippen } from "../design/antippen";
import { Button } from "react-aria-components";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument, type Zeile } from "../plattform/lesemodelle";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { Abschnitt, Leer, name, wert, useRevier, useRevierSammlung, type Person } from "./teile";

/**
 * Freigabe, Kontingentstand, Kontrollliste und Prüfprotokoll (oberflaeche.md §6, F-101, F-144, F-071). Sperren,
 * Entsperren und Stückzahl sind Befehle des Aggregats Freigabe; ob die Person sie darf, entscheidet der Server.
 */

export function Freigabe() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const revier = useRevier(id);
  const positionen = useRevierSammlung(id, revier, "freigabe");
  const kontingente = useRevierSammlung(id, revier, "kontingentstand");
  const [grund, setGrund] = useState<string | null>(null);
  const [stueckzahl, setStueckzahl] = useState<Zeile | null>(null);
  const [festlegen, setFestlegen] = useState(false);
  const basis = { "Drückjagd": id, Revier: revier };
  async function senden(pfad: string, daten: Record<string, unknown>) {
    setGrund(null);
    const a = await befehl(pfad, { ...basis, ...daten });
    if (!a.ok) setGrund(abweisungsgrund(a));
    return a.ok;
  }
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Freigabe" unten={
      <div className="knopfreihe liste__unten">
        <Knopf art="planung" symbol="kontingent" onPress={() => navigate(`/drueckjagd/${id}/kontingentstand`)}>Kontingentstand</Knopf>
        <Knopf art="planung" symbol="freigabe" onPress={() => setFestlegen(true)}>Festlegen</Knopf>
      </div>
    }>
      <Abweisung grund={grund} />
      {positionen.daten.length === 0 && !positionen.laedt ? <Leer text="Noch keine Freigabe" symbol="freigabe" /> : null}
      {positionen.daten.map((p) => {
        const gesperrt = p["Freigabestatus"] === "gesperrt";
        return (
          <article key={p._id} className="eintrag platte" aria-label={String(p["Wildart"])}>
            <h3><Symbol name={String(p["Wildart"]).toLowerCase()} />{String(p["Wildart"])}
              <span className={gesperrt ? "marke-status marke-status--stark" : "marke-status"}>{gesperrt ? <Symbol name="sperre" /> : null}{String(p["Freigabestatus"])}</span></h3>
            <div>{wert(p["Freigabetext"])}</div>
            <div className="eintrag__meta">Meldepflicht: {wert(p["Meldepflicht"])}</div>
            <div className="knopfreihe">
              <Knopf art="planung-zweit" symbol="sperre" onPress={() => void senden("FreigabepositionSperren", { Wildart: p["Wildart"] })}>Sperren</Knopf>
              <Knopf art="planung-zweit" symbol="freigeben" onPress={() => void senden("SperreAufheben", { Wildart: p["Wildart"] })}>Entsperren</Knopf>
            </div>
          </article>
        );
      })}
      {kontingente.daten.map((k) => (
        <article key={k._id} className="eintrag platte" aria-label={String(k["Kontingent"])}>
          <h3><Symbol name="kontingent" />{String(k["Kontingent"])}<span className="marke-status">{String(k["Status"])}</span></h3>
          <div>noch {String(k["Rest"])} von {String(k["Stückzahl"])} · {((k["Ansprachen"] as string[] | undefined) ?? []).join(", ")}</div>
          {k["Anzeige"] ? <Status symbol="anschuss">{String(k["Anzeige"])}</Status> : null}
          <div className="knopfreihe">
            <Knopf art="planung-zweit" symbol="sperre" onPress={() => void senden("KontingentSperren", { Kontingent: k["Kontingent"] })}>Sperren</Knopf>
            <Knopf art="planung-zweit" symbol="freigeben" onPress={() => void senden("KontingentsperreAufheben", { Kontingent: k["Kontingent"] })}>Entsperren</Knopf>
            <Knopf art="planung-zweit" symbol="korrigieren" onPress={() => setStueckzahl(k)}>Stückzahl</Knopf>
          </div>
        </article>
      ))}
      <StueckzahlDialog kontingent={stueckzahl} onSchliessen={() => setStueckzahl(null)} senden={senden} />
      <FreigabeDialog offen={festlegen} positionen={positionen.daten} kontingente={kontingente.daten} onSchliessen={() => setFestlegen(false)} senden={senden} />
    </AnsichtDerDrueckjagd>
  );
}

type Senden = (pfad: string, daten: Record<string, unknown>) => Promise<boolean>;

function StueckzahlDialog({ kontingent, onSchliessen, senden }: { kontingent: Zeile | null; onSchliessen: () => void; senden: Senden }) {
  const [zahl, setZahl] = useState("");
  return (
    <Dialog name="Stückzahl" symbol="kontingent" offen={!!kontingent} onSchliessen={onSchliessen}>
      <div className="formular">
        <Textfeld label="Stückzahl" wert={zahl} onChange={setZahl} eingabemodus="numeric" />
        <div className="knopfreihe">
          <Knopf onPress={async () => { if (await senden("StueckzahlAendern", { Kontingent: kontingent?.["Kontingent"], "Stückzahl": Number(zahl) })) { setZahl(""); onSchliessen(); } }}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

/** Freigabe festlegen: die bestehende Freigabe plus eine Position bzw. ein Kontingent (vollständige neue Freigabe). */
function FreigabeDialog({ offen, positionen, kontingente, onSchliessen, senden }: { offen: boolean; positionen: Zeile[]; kontingente: Zeile[]; onSchliessen: () => void; senden: Senden }) {
  const [wildart, setWildart] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [melde, setMelde] = useState(false);
  const [kontingent, setKontingent] = useState("");
  const [ansprachen, setAnsprachen] = useState<string[]>([]);
  const [zahl, setZahl] = useState("");
  async function speichern() {
    const alle = positionen.map((p) => ({
      Wildart: p["Wildart"], Freigabetext: p["Freigabetext"] ?? undefined, Meldepflicht: p["Meldepflicht"], Freigabestatus: p["Freigabestatus"],
      Kontingente: kontingente.filter((k) => k["Wildart"] === p["Wildart"]).map((k) => ({ Kontingent: k["Kontingent"], Ansprachen: k["Ansprachen"], "Stückzahl": k["Stückzahl"] })),
    })) as Array<Record<string, unknown> & { Kontingente: unknown[] }>;
    let pos = alle.find((p) => p.Wildart === wildart);
    if (!pos) { pos = { Wildart: wildart, Freigabetext: text || undefined, Meldepflicht: melde, Kontingente: [] }; alle.push(pos); }
    if (kontingent) pos.Kontingente.push({ Kontingent: kontingent, Ansprachen: ansprachen, "Stückzahl": Number(zahl) });
    if (await senden("FreigabeFestlegen", { Freigabepositionen: alle })) { setWildart(null); setText(""); setKontingent(""); setAnsprachen([]); setZahl(""); onSchliessen(); }
  }
  return (
    <Dialog name="Freigabe" symbol="freigabe" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        <Auswahl label="Wildart" optionen={WILDARTEN} wert={wildart} onChange={setWildart} />
        <Textfeld label="Freigabetext" wert={text} onChange={setText} />
        <Ankreuzfeld label="Meldepflicht" an={melde} onChange={setMelde} />
        <Textfeld label="Kontingent" wert={kontingent} onChange={setKontingent} />
        {wildart ? (
          <div className="filter" role="group" aria-label="Ansprachen">
            {ANSPRACHEN[wildart as Wildart].map((a) => <Ankreuzfeld key={a} label={a} an={ansprachen.includes(a)} onChange={(an) => setAnsprachen(an ? [...ansprachen, a] : ansprachen.filter((x) => x !== a))} />)}
          </div>
        ) : null}
        <Textfeld label="Stückzahl" wert={zahl} onChange={setZahl} eingabemodus="numeric" />
        <div className="knopfreihe">
          <Knopf onPress={speichern} isDisabled={!wildart}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

export function Kontingentstand() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const kontingente = useRevierSammlung(id, revier, "kontingentstand");
  const spalten = ["Kontingent", "Wildart", "Ansprachen", "Stückzahl", "erlegt", "Rest", "offene Anschüsse", "Status"];
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Kontingentstand">
      <table className="tabelle">
        <thead><tr>{spalten.map((s) => <th key={s} scope="col">{s}</th>)}</tr></thead>
        <tbody>{kontingente.daten.map((k) => <tr key={k._id}>{spalten.map((s) => <td key={s}>{wert(k[s])}</td>)}</tr>)}</tbody>
      </table>
      {kontingente.daten.filter((k) => k["Anzeige"]).map((k) => <Status key={k._id} symbol="anschuss">{String(k["Anzeige"])}</Status>)}
    </AnsichtDerDrueckjagd>
  );
}

export function Kontrollliste() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const revier = useRevier(id);
  const liste = useRevierSammlung(id, revier, "kontrollliste");
  const rg = useRueckgaengig();
  const zeilen = [...liste.daten].sort((a, b) => name(a["Person"]).localeCompare(name(b["Person"]), "de"));
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Kontrollliste"
      unten={rg.offen ? <RueckgaengigLeiste offen={rg.offen} onRueckgaengig={rg.zuruecknehmen} /> : undefined}>
      {rg.offen && !rg.offen.abgelaufen ? <Bestaetigung offen={rg.offen} /> : null}
      {/* Referenz `#s-kontrolle`: je Person eine Zeile als Ziel mit dem Namen; Tabelle nach Vertrag §6 (Spalten Person,
          Stand der Nachweiskontrolle, Geprüft durch, geprüft am), bei schmaler Ansicht als Zeilen mit Werten nebeneinander */}
      <section className="liste-bereich platte" role="region" aria-label="Kontrollliste">
        <table className="tabelle tabelle--breit tabelle--kontrolle" role="table">
          <thead role="rowgroup"><tr role="row">{["Person", "Stand der Nachweiskontrolle", "Geprüft durch", "geprüft am"].map((s) => <th key={s} scope="col" role="columnheader">{s}</th>)}</tr></thead>
          <tbody role="rowgroup">
            {zeilen.map((z) => (
              <tr key={z._id} role="row">
                <td role="cell"><ZeileKnopf symbol="schuetze" titel={name(z["Person"])} onPress={() => navigate(`/drueckjagd/${id}/kontrollliste/${z._id}`)} /></td>
                <td role="cell">{wert(z["Stand der Nachweiskontrolle"])}</td><td role="cell">{name(z["Geprüft durch"])}</td><td role="cell">{wert(z["geprüft am"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {zeilen.length === 0 && !liste.laedt ? <Leer text="Niemand führt eine Waffe" symbol="kontrolle" /> : null}
    </AnsichtDerDrueckjagd>
  );
}

interface Vorbelegung {
  Jagdschein: { "gültig bis": string; Jagdscheinart?: string } | null;
  "Schießnachweis": { "Datum des Nachweises": string } | null;
}

/**
 * Befehle für „in Ordnung“ (oberflaeche.md §5, F-074): übernimmt die letzten Prüfergebnisse der Person aus früheren
 * Drückjagden des Reviers (`_vorbelegung` der Kontrollliste) für jede Prüfung, die hier noch nicht in Ordnung ist.
 * `null`, wenn eine nötige Prüfung keine verwendbare Vorbelegung hat; dann wird sie einzeln erfasst.
 */
function befehleInOrdnung(zeile: Zeile, basis: Record<string, unknown>): Array<{ befehl: string; pruefung: string; daten: Record<string, unknown> }> | null {
  const vb = (zeile["_vorbelegung"] as Vorbelegung | undefined) ?? { Jagdschein: null, "Schießnachweis": null };
  const befehle: Array<{ befehl: string; pruefung: string; daten: Record<string, unknown> }> = [];
  if (zeile["Jagdschein"] !== "gültig vorgelegt") {
    if (!vb.Jagdschein) return null;
    befehle.push({ befehl: "JagdscheinGeprueftErfassen", pruefung: "Jagdschein", daten: { ...basis, Ergebnis: "gültig vorgelegt", ...vb.Jagdschein } });
  }
  if (zeile["Schießnachweis"] !== "vorgelegt" && zeile["Schießnachweis"] !== "nicht erforderlich") {
    if (!vb["Schießnachweis"]) return null;
    befehle.push({ befehl: "SchiessnachweisGeprueftErfassen", pruefung: "Schießnachweis", daten: { ...basis, Ergebnis: "vorgelegt", ...vb["Schießnachweis"] } });
  }
  return befehle.length ? befehle : null;
}

/**
 * Nachweiskontrolle einer Person (oberflaeche.md §3.2, §6): Ansicht mit dem Namen der Person. „in Ordnung“ erfasst die
 * vorbelegten Prüfungen als Meldung mit Rückgängig (ADR-0023) über die Warteschlange und kehrt zur Kontrollliste zurück;
 * „Jagdschein“, „Schießnachweis“ und „Korrigieren“ öffnen je einen Dialog.
 */
export function NachweiskontrolleDerPerson() {
  const { id = "", teilnehmer = "" } = useParams();
  const navigate = useNavigate();
  const revier = useRevier(id);
  const zeile = useDokument(revier ? `drueckjagden/${id}/reviere/${revier}/kontrollliste/${teilnehmer}` : null);
  const rg = useRueckgaengig();
  const [art, setArt] = useState<null | "Jagdschein" | "Schießnachweis" | "Korrigieren">(null);
  const zurueck = useZurueck(`/drueckjagd/${id}/kontrollliste`);
  const p = zeile.daten?.["Person"] as Person | undefined;
  const basis = { "Drückjagd": id, Revier: revier, Teilnehmer: teilnehmer };
  const inOrdnung = zeile.daten ? befehleInOrdnung(zeile.daten, basis) : null;
  function inOrdnungMelden() {
    if (!inOrdnung || !p) return;
    const [erster, ...weitere] = inOrdnung;
    rg.vormerken({
      befehl: erster!.befehl, begriff: "in Ordnung", symbol: "jagdschein", angaben: [["teilnehmer", p.Name]], titel: () => `${erster!.pruefung} ${p.Name}`,
      daten: erster!.daten, weitere: weitere.map((w) => ({ befehl: w.befehl, titel: `${w.pruefung} ${p.Name}`, daten: w.daten })),
    });
    navigate(`/drueckjagd/${id}/kontrollliste`);
  }
  // Referenz `#s-kontrolle-person` (design/README §9.6, kurzer Ablauf): „Jagdschein“ und „Schießnachweis“ öffnen ihren
  // Dialog (R1), „Korrigieren“ darunter, „in Ordnung“ meldet in R3 (mit Rückgängig), außerhalb der Abdrücke von Tipp 1.
  const d = zeile.daten;
  const bis = (w: unknown) => (w ? `bis ${wert(w)}` : null);
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name={p?.Name ?? "Nachweiskontrolle"} art="ablauf" klassen="ablauf--kurz"
      titel={p?.Name ?? "Nachweiskontrolle"} schritte={d ? `Kontrolle · ${wert(d["Stand der Nachweiskontrolle"])}` : "Kontrolle"}>
      <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={zurueck} />
      <div className="r12">
        <div className="raster raster--2" data-gleich="">
          <Kachel symbol="jagdschein" begriff="Jagdschein" art="wahl" onPress={() => setArt("Jagdschein")}
            unter={d ? [wert(d["Jagdschein"]), bis(d["vorbelegt gültig bis"])].filter(Boolean).join(" · ") : null} />
          <Kachel symbol="schiessnachweis" begriff="Schießnachweis" art="wahl" onPress={() => setArt("Schießnachweis")}
            unter={d ? wert(d["Schießnachweis"]) : null} />
        </div>
        <Kachel symbol="korrigieren" begriff="Korrigieren" art="kompakt" onPress={() => setArt("Korrigieren")} />
      </div>
      <div className="r3">
        <Kachel symbol="haken" begriff="in Ordnung" art="wahl" className="k--breit" isDisabled={!inOrdnung} onPress={inOrdnungMelden} />
      </div>
      <NachweisDialog basis={basis} art={art} zeile={d} onSchliessen={() => setArt(null)} />
    </AnsichtDerDrueckjagd>
  );
}

/** Zeile als Ziel in einer Liste (design/README §9.8, Referenz `zeile--knopf`): Symbol, Name, Pfeil; Name = Wert. */
function ZeileKnopf({ symbol, titel, onPress }: { symbol: string; titel: string; onPress: () => void }) {
  return (
    <Button className="zeile zeile--knopf" aria-label={titel} onPress={(e) => { if (istAntippen(e)) onPress(); }}>
      <Symbol name={symbol} />
      <span className="zeile__t">{titel}</span>
      <Symbol name="weiter" className="zeile__pfeil" />
    </Button>
  );
}

interface Erfasst {
  "gültig bis"?: string | null;
  "Datum des Nachweises"?: string | null;
  Prüfung?: "Jagdschein" | "Schießnachweis" | null;
}

function korrekturVonSchiessnachweis(zeile: Zeile | null | undefined, ergebnis: string | null): boolean {
  if (ergebnis && (ERGEBNISSE_SCHIESSNACHWEIS as readonly string[]).includes(ergebnis) && !(ERGEBNISSE_JAGDSCHEIN as readonly string[]).includes(ergebnis)) return true;
  if (ergebnis && (ERGEBNISSE_JAGDSCHEIN as readonly string[]).includes(ergebnis) && !(ERGEBNISSE_SCHIESSNACHWEIS as readonly string[]).includes(ergebnis)) return false;
  const erfasst = zeile?.["_erfasst"] as Erfasst | undefined;
  const pruefung = erfasst?.Prüfung ?? (zeile?.["Prüfung"] as string | undefined);
  if (pruefung === "Schießnachweis") return true;
  if (pruefung === "Jagdschein") return false;
  const sn = Boolean(erfasst?.["Datum des Nachweises"] || zeile?.["Datum des Nachweises"]
    || (zeile?.["Schießnachweis"] && !["offen", "nicht geprüft", "nicht erforderlich"].includes(String(zeile["Schießnachweis"]))));
  const js = Boolean(erfasst?.["gültig bis"] || zeile?.["gültig bis"]
    || (zeile?.["Jagdschein"] && !["offen", "nicht geprüft"].includes(String(zeile["Jagdschein"]))));
  return sn && !js;
}

function vorbelegtesDatum(zeile: Zeile | null | undefined, art: null | "Jagdschein" | "Schießnachweis" | "Korrigieren", feld: string): string {
  const vb = (zeile?.["_vorbelegung"] as Vorbelegung | undefined) ?? { Jagdschein: null, "Schießnachweis": null };
  const erfasst = zeile?.["_erfasst"] as Erfasst | undefined;
  if (feld === "gültig bis") {
    if (art === "Korrigieren") return String(erfasst?.["gültig bis"] ?? zeile?.["gültig bis"] ?? vb.Jagdschein?.["gültig bis"] ?? zeile?.["vorbelegt gültig bis"] ?? "");
    return String(vb.Jagdschein?.["gültig bis"] ?? zeile?.["vorbelegt gültig bis"] ?? zeile?.["gültig bis"] ?? "");
  }
  if (feld === "Datum des Nachweises") {
    if (art === "Korrigieren") return String(erfasst?.["Datum des Nachweises"] ?? zeile?.["Datum des Nachweises"] ?? vb["Schießnachweis"]?.["Datum des Nachweises"] ?? "");
    return String(vb["Schießnachweis"]?.["Datum des Nachweises"] ?? zeile?.["Datum des Nachweises"] ?? "");
  }
  return "";
}

export function NachweisDialog({ basis, art, zeile, onSchliessen }: {
  basis: Record<string, unknown>; art: null | "Jagdschein" | "Schießnachweis" | "Korrigieren"; zeile?: Zeile | null; onSchliessen: () => void;
}) {
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const [felder, setFelder] = useState<Record<string, string>>({});
  const [jagdscheinart, setJagdscheinart] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const istSchiessnachweis = art === "Schießnachweis" || (art === "Korrigieren" && korrekturVonSchiessnachweis(zeile, ergebnis));
  const datumsfeld = istSchiessnachweis ? "Datum des Nachweises" : "gültig bis";
  const feldwert = (f: string) => f in felder ? felder[f]! : vorbelegtesDatum(zeile, art, f);
  function schliessen() {
    setErgebnis(null); setFelder({}); setJagdscheinart(null); setGrund(null);
    onSchliessen();
  }
  async function speichern() {
    setGrund(null);
    const daten: Record<string, unknown> = { ...basis, Ergebnis: ergebnis };
    const datum = feldwert(datumsfeld);
    if (datum) daten[datumsfeld] = datum;
    const textfeld = art === "Korrigieren" ? "Grund" : "Bemerkung";
    if (felder[textfeld]) daten[textfeld] = felder[textfeld];
    if (jagdscheinart && art === "Jagdschein") daten["Jagdscheinart"] = jagdscheinart;
    const pfad = art === "Jagdschein" ? "JagdscheinGeprueftErfassen" : art === "Schießnachweis" ? "SchiessnachweisGeprueftErfassen" : "KontrolleKorrigieren";
    const a = await befehl(pfad, daten);
    if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    schliessen();
  }
  const feld = (f: string, typ: "text" | "date" = "text") => (
    <Textfeld key={f} label={f} wert={feldwert(f)} onChange={(v) => setFelder({ ...felder, [f]: v })} typ={typ} />
  );
  return (
    <Dialog name={art ?? "Nachweiskontrolle"} symbol={art === "Schießnachweis" ? "schiessnachweis" : art === "Korrigieren" ? "korrigieren" : "jagdschein"} offen={!!art} onSchliessen={schliessen}>
      <div className="formular">
        <Auswahl label="Ergebnis" optionen={art === "Schießnachweis" ? ERGEBNISSE_SCHIESSNACHWEIS : art === "Jagdschein" ? ERGEBNISSE_JAGDSCHEIN
          : [...ERGEBNISSE_JAGDSCHEIN, ...ERGEBNISSE_SCHIESSNACHWEIS.filter((e) => e !== "nicht vorgelegt")]} wert={ergebnis} onChange={setErgebnis} />
        {!istSchiessnachweis ? feld("gültig bis", "date") : null}
        {art === "Jagdschein" ? <Klappliste label="Jagdscheinart" wert={jagdscheinart} leer="—" onChange={setJagdscheinart} optionen={JAGDSCHEINARTEN.map((j) => ({ wert: j, text: j }))} /> : null}
        {istSchiessnachweis ? feld("Datum des Nachweises", "date") : null}
        {art === "Korrigieren" ? feld("Grund") : feld("Bemerkung")}
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={schliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

export function Pruefprotokoll() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const liste = useRevierSammlung(id, revier, "pruefprotokoll");
  const spalten = ["geprüft am", "Geprüft durch", "Person", "Prüfung", "Ergebnis", "gültig bis", "Grund"];
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Prüfprotokoll">
      <Abschnitt name="Prüfeinträge" symbol="jagdschein">
        <table className="tabelle">
          <thead><tr>{spalten.map((s) => <th key={s} scope="col">{s}</th>)}</tr></thead>
          <tbody>
            {liste.daten.map((z) => (
              <tr key={z._id} aria-label={name(z["Person"])}>
                {spalten.map((s) => <td key={s}>{s === "Person" || s === "Geprüft durch" ? name(z[s]) : wert(z[s])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </Abschnitt>
    </AnsichtDerDrueckjagd>
  );
}
